import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : '/api';

// ── Design tokens ─────────────────────────────────────────────────────────
const T = {
  green:     '#1B5E20',
  green2:    '#144A18',
  greenSoft: '#E8F5E9',
  red:       '#C62828',
  redSoft:   '#FFEBEE',
  bg:        '#f7f7f7',
  ink:       '#1A1A1A',
  ink2:      '#4A4A4A',
  ink3:      '#7A7A7A',
  ink4:      '#A6A6A6',
  line:      '#E6E6E6',
  line2:     '#EFEFEF',
  card:      '#FFFFFF',
  amberBg:   '#FFF3E0',
  amberInk:  '#E65100',
  blueBg:    '#E3F2FD',
  blueInk:   '#0D47A1',
  shadow:    '0 1px 2px rgba(15,15,15,.04)',
  radius:    12,
};

// ── Helpers ───────────────────────────────────────────────────────────────
const fmt = n => new Intl.NumberFormat('fr-FR').format(Math.round(n || 0));
const fmtFCFA = n => fmt(n) + ' FCFA';
const pctCalc = (r, i) => i > 0 ? Math.min(100, Math.round(r / i * 100)) : 0;
const toArray = d => Array.isArray(d) ? d : (d?.rows || d?.data || []);
const aujourdhui = () => new Date().toISOString().split('T')[0];

// ── SVG icons ─────────────────────────────────────────────────────────────
function Ico({ name, size = 16, color = 'currentColor', stroke = 2 }) {
  const s = { width: size, height: size };
  const p = { fill: 'none', stroke: color, strokeWidth: stroke, strokeLinecap: 'round', strokeLinejoin: 'round' };
  const paths = {
    eye:      <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z" {...p}/><circle cx="12" cy="12" r="3" {...p}/></>,
    payment:  <><rect x="2" y="6" width="20" height="14" rx="2" {...p}/><path d="M2 12h20" {...p}/></>,
    more:     <><circle cx="5" cy="12" r="1.5" fill={color}/><circle cx="12" cy="12" r="1.5" fill={color}/><circle cx="19" cy="12" r="1.5" fill={color}/></>,
    plus:     <path d="M12 5v14M5 12h14" {...p}/>,
    back:     <><path d="M19 12H5" {...p}/><path d="M12 19l-7-7 7-7" {...p}/></>,
    search:   <><circle cx="11" cy="11" r="7" {...p}/><path d="m21 21-4.3-4.3" {...p}/></>,
    export:   <><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" {...p}/><path d="M7 10l5 5 5-5" {...p}/><path d="M12 15V3" {...p}/></>,
    edit:     <><path d="M12 20h9" {...p}/><path d="M16.5 3.5a2.12 2.12 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" {...p}/></>,
    pause:    <><rect x="6" y="4" width="4" height="16" {...p}/><rect x="14" y="4" width="4" height="16" {...p}/></>,
    trash:    <><path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" {...p}/></>,
    check:    <><path d="M20 6L9 17l-5-5" {...p}/></>,
    clock:    <><circle cx="12" cy="12" r="10" {...p}/><path d="M12 7v5l3 2" {...p}/></>,
    alert:    <><path d="M10.3 3.9L2 18a2 2 0 0 0 1.7 3h16.6A2 2 0 0 0 22 18L13.7 3.9a2 2 0 0 0-3.4 0z" {...p}/><path d="M12 9v4M12 17h.01" {...p}/></>,
    dollar:   <path d="M12 2v20M17 5H9.5a3.5 3.5 0 1 0 0 7H14a3.5 3.5 0 1 1 0 7H6" {...p}/>,
    card:     <><rect x="3" y="5" width="18" height="14" rx="2" {...p}/><path d="M3 10h18" {...p}/></>,
    calendar: <><rect x="3" y="4" width="18" height="18" rx="2" {...p}/><path d="M16 2v4M8 2v4M3 10h18" {...p}/></>,
    history:  <><path d="M3 12a9 9 0 1 0 3-6.7" {...p}/><path d="M3 4v5h5" {...p}/><path d="M12 7v5l3 2" {...p}/></>,
    info:     <><circle cx="12" cy="12" r="10" {...p}/><path d="M12 8v4M12 16h.01" {...p}/></>,
    shield:   <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" {...p}/>,
    file:     <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" {...p}/><path d="M14 2v6h6" {...p}/></>,
    play:     <polygon points="5 3 19 12 5 21 5 3" {...p}/>,
    calcok:   <><path d="M9 11l3 3 8-8" {...p}/><path d="M20 12v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h10" {...p}/></>,
    map:      <><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" {...p}/><circle cx="12" cy="10" r="3" {...p}/></>,
    chart:    <><path d="M3 3v18h18" {...p}/><path d="M7 15l4-4 3 3 5-6" {...p}/></>,
    x:        <><path d="M18 6L6 18" {...p}/><path d="M6 6l12 12" {...p}/></>,
  };
  return <svg viewBox="0 0 24 24" style={s}>{paths[name] || null}</svg>;
}

// ── BadgeStatut ───────────────────────────────────────────────────────────
function BadgeStatut({ statut }) {
  const cfg = {
    active:     { label: 'Active',     bg: '#E8F5E9', color: '#1B5E20', dot: '#1B5E20' },
    en_attente: { label: 'En attente', bg: '#FFF3E0', color: '#E65100', dot: '#E65100' },
    validee:    { label: 'Validée',    bg: '#E3F2FD', color: '#0D47A1', dot: '#0D47A1' },
    suspendue:  { label: 'Suspendue', bg: '#F5F5F5', color: '#616161', dot: '#9E9E9E' },
    soldee:     { label: 'Soldée',    bg: '#EEEEEE', color: '#424242', dot: '#616161' },
    refusee:    { label: 'Refusée',   bg: '#FFEBEE', color: '#B71C1C', dot: '#B71C1C' },
    annulee:    { label: 'Annulée',   bg: '#FAFAFA', color: '#9E9E9E', dot: '#BDBDBD' },
  };
  const c = cfg[statut] || cfg.en_attente;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: c.bg, color: c.color,
      padding: '3px 9px', borderRadius: 999, fontSize: 11.5, fontWeight: 600, whiteSpace: 'nowrap' }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: c.dot, flexShrink: 0 }} />
      {c.label}
    </span>
  );
}

// ── BadgeType ─────────────────────────────────────────────────────────────
function BadgeType({ type, libelle }) {
  const dots = {
    pret_social:        '#1B5E20',
    avance_salaire:     '#6A1B9A',
    pret_materiel:      '#8D6E63',
    caution:            '#37474F',
    os_aide_scolaire:   '#0288D1',
    os_aide_medicale:   '#00796B',
    os_aide_logement:   '#5D4037',
    ipm_facture:        '#E65100',
    ipm_hospitalisation:'#AD1457',
    pret_sogas:         '#1565C0',
  };
  const dot = dots[type] || '#757575';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '3px 9px', borderRadius: 6, fontSize: 12, fontWeight: 500,
      background: '#F3F5F3', color: T.ink2, border: '0.5px solid #E6EAE6' }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: dot, flexShrink: 0 }} />
      {libelle || type}
    </span>
  );
}

// ── BarreMini ─────────────────────────────────────────────────────────────
function BarreMini({ rembourse, initial, restant, statut }) {
  const p = pctCalc(rembourse, initial);
  const barColor = statut === 'soldee' ? '#616161' : statut === 'suspendue' ? '#9E9E9E' : T.green;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 120 }}>
      <div style={{ fontSize: 11, color: T.ink3, fontVariantNumeric: 'tabular-nums' }}>
        <strong style={{ color: T.ink }}>{fmt(restant)}</strong> · {p} %
      </div>
      <div style={{ height: 5, background: '#EFEFEF', borderRadius: 99, overflow: 'hidden' }}>
        <div style={{ width: p + '%', height: '100%', background: barColor, borderRadius: 99 }} />
      </div>
    </div>
  );
}

// ── MetriqueCard ──────────────────────────────────────────────────────────
function MetriqueCard({ icone, label, valeur, sous, accent, alerte, pending }) {
  const icoBg = alerte ? '#FFF3E0' : pending ? T.blueBg : T.greenSoft;
  const icoColor = alerte ? '#E65100' : pending ? T.blueInk : T.green;
  return (
    <div style={{ background: T.card, border: `0.5px solid ${T.line}`, borderRadius: T.radius,
      padding: '16px 18px', boxShadow: T.shadow, display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: T.ink3, fontSize: 12.5, fontWeight: 500 }}>
        <div style={{ width: 26, height: 26, borderRadius: 7, background: icoBg, color: icoColor,
          display: 'grid', placeItems: 'center' }}>
          <Ico name={icone} size={14} color={icoColor} />
        </div>
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.01em', color: T.ink, fontVariantNumeric: 'tabular-nums' }}>
        {valeur}
      </div>
      {sous && <div style={{ fontSize: 12, color: alerte ? '#E65100' : T.ink3 }}>{sous}</div>}
    </div>
  );
}

// ── BreadcrumbRow ─────────────────────────────────────────────────────────
function BreadcrumbRow({ chemin, setVue }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: T.ink3, marginBottom: 8 }}>
      <span onClick={() => setVue('liste')} style={{ color: T.ink3, cursor: 'pointer' }}>
        Prêts et retenues
      </span>
      {chemin && (
        <>
          <span style={{ color: T.ink4 }}>/</span>
          <span style={{ color: T.ink, fontWeight: 500 }}>{chemin}</span>
        </>
      )}
    </div>
  );
}

// ── Topbar + segmented control ────────────────────────────────────────────
function Topbar({ vue, setVue }) {
  const segs = [
    { key: 'liste',   label: 'Liste' },
    { key: 'detail',  label: 'Détail' },
    { key: 'nouveau', label: 'Nouveau' },
  ];
  return (
    <div style={{ background: '#fff', borderBottom: `1px solid ${T.line}`, padding: '12px 24px',
      display: 'flex', alignItems: 'center', gap: 14, position: 'sticky', top: 0, zIndex: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: T.ink3 }}>
        <span>Finance</span>
        <span style={{ color: T.ink4 }}>/</span>
        <span style={{ color: T.ink, fontWeight: 500 }}>Prêts et retenues</span>
      </div>
      <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
        <div style={{ display: 'inline-flex', background: '#fff', border: `1px solid ${T.line}`, borderRadius: 10, padding: 3 }}>
          {segs.map(s => (
            <button key={s.key} onClick={() => setVue(s.key)} style={{
              border: 0, borderRadius: 7, padding: '7px 12px', cursor: 'pointer',
              fontSize: 13, fontWeight: 500,
              background: vue === s.key ? T.green : 'transparent',
              color: vue === s.key ? '#fff' : T.ink2,
            }}>
              {s.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── BanniereMsg — feedback global ─────────────────────────────────────────
function BanniereMsg({ msg }) {
  if (!msg?.texte) return null;
  const succes = msg.type === 'succes';
  return (
    <div style={{
      background: succes ? '#E8F5E9' : '#FFEBEE',
      color: succes ? T.green : T.red,
      border: `0.5px solid ${succes ? '#A5D6A7' : '#EF9A9A'}`,
      borderRadius: 8, padding: '10px 16px', marginBottom: 16,
      fontSize: 13, fontWeight: 500,
    }}>
      {msg.texte}
    </div>
  );
}

// ── RechercheEmploye — picker employé pour VueFormulaire ─────────────────
function RechercheEmploye({ employe, onSelect, token }) {
  const [recherche, setRecherche] = useState('');
  const [resultats, setResultats] = useState([]);
  const [ouvert, setOuvert] = useState(false);
  const [chargement, setChargement] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (recherche.length < 2) { setResultats([]); return; }
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      setChargement(true);
      try {
        const res = await axios.get(`${API}/employes`, {
          params: { recherche, statut: 'Actif', limite: 8 },
          headers: { Authorization: `Bearer ${token}` },
        });
        const liste = toArray(res.data.employes || res.data);
        setResultats(liste);
      } catch (_) {}
      finally { setChargement(false); }
    }, 300);
    return () => clearTimeout(timerRef.current);
  }, [recherche, token]);

  const initiales = e => `${(e.prenom || '')[0] || ''}${(e.nom || '')[0] || ''}`.toUpperCase();

  if (employe) {
    return (
      <div style={{ border: `1px solid ${T.line}`, borderRadius: 8, padding: '10px 12px',
        display: 'flex', alignItems: 'center', gap: 10, background: '#fff' }}>
        <div style={{ width: 34, height: 34, borderRadius: '50%', background: T.greenSoft, color: T.green2,
          display: 'grid', placeItems: 'center', fontWeight: 700, fontSize: 12, flexShrink: 0 }}>
          {initiales(employe)}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: 13 }}>{employe.prenom} {employe.nom}</div>
          <div style={{ fontSize: 12, color: T.ink3 }}>
            {employe.matricule}{employe.salaire_base ? ` · ${fmtFCFA(employe.salaire_base)}` : ' · Salaire non renseigné'}
          </div>
        </div>
        <button onClick={() => onSelect(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.ink3, padding: 4 }}>
          <Ico name="x" size={14} color={T.ink3} />
        </button>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      <div style={{ position: 'relative' }}>
        <div style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)' }}>
          <Ico name="search" size={14} color={T.ink3} />
        </div>
        <input
          value={recherche}
          onChange={e => { setRecherche(e.target.value); setOuvert(true); }}
          onFocus={() => setOuvert(true)}
          placeholder="Nom, prénom ou matricule…"
          style={{ width: '100%', padding: '10px 12px 10px 34px', border: `1px solid ${T.line}`,
            borderRadius: 8, background: '#fff', fontSize: 13, boxSizing: 'border-box' }}
        />
        {chargement && (
          <div style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 11, color: T.ink3 }}>
            ...
          </div>
        )}
      </div>
      {ouvert && resultats.length > 0 && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff',
          border: `1px solid ${T.line}`, borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,.08)',
          zIndex: 50, maxHeight: 240, overflowY: 'auto', marginTop: 2 }}>
          {resultats.map(e => (
            <div key={e.id}
              onClick={() => { onSelect(e); setOuvert(false); setRecherche(''); setResultats([]); }}
              style={{ padding: '10px 12px', cursor: 'pointer', display: 'flex', gap: 10, alignItems: 'center',
                borderBottom: `1px solid ${T.line2}` }}
              onMouseEnter={ev => ev.currentTarget.style.background = '#f7f7f7'}
              onMouseLeave={ev => ev.currentTarget.style.background = 'transparent'}>
              <div style={{ width: 30, height: 30, borderRadius: '50%', background: T.greenSoft, color: T.green2,
                display: 'grid', placeItems: 'center', fontWeight: 700, fontSize: 11, flexShrink: 0 }}>
                {initiales(e)}
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{e.prenom} {e.nom}</div>
                <div style={{ fontSize: 11.5, color: T.ink3 }}>{e.matricule}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── CardSolvabilite ───────────────────────────────────────────────────────
function CardSolvabilite({ solvabilite, loading, mensualite }) {
  if (loading) {
    return (
      <div style={{ border: `1px solid ${T.line}`, borderRadius: 8, padding: '14px 16px', background: T.bg,
        fontSize: 13, color: T.ink3, textAlign: 'center' }}>
        Calcul de la solvabilité…
      </div>
    );
  }
  if (!solvabilite) return null;

  const { salaire_base, total_dettes_actives, capacite_retenue, detail_par_gestionnaire } = solvabilite;
  const mensuel = parseFloat(mensualite) || 0;
  const totalAvec = total_dettes_actives + mensuel;
  const pct = salaire_base > 0 ? Math.round((totalAvec / salaire_base) * 100) : 0;
  const risque = pct >= 50;
  const attention = pct >= 40 && pct < 50;
  const couleur = risque ? T.red : attention ? '#EF9F27' : T.green;
  const bg = risque ? '#FFF5F5' : attention ? '#FFFBF0' : T.greenSoft;

  return (
    <div style={{ border: `1px solid ${T.line}`, borderLeft: `4px solid ${couleur}`,
      borderRadius: 8, padding: '14px 16px', background: bg }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: T.ink, marginBottom: 10 }}>Solvabilité</div>
      <div style={{ fontSize: 12.5, color: T.ink2, marginBottom: 8 }}>
        Salaire net : <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtFCFA(salaire_base)}</strong>
      </div>
      <div style={{ borderTop: `1px solid ${couleur}22`, paddingTop: 8, marginBottom: 8 }}>
        {toArray(detail_par_gestionnaire).map(d => (
          <div key={d.gestionnaire} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, color: T.ink2, padding: '2px 0' }}>
            <span>Retenues {d.gestionnaire === 'rh' ? 'RH' : d.gestionnaire === 'oeuvre_sociale' ? 'OS' : 'IPM'}</span>
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtFCFA(d.total_gestionnaire)}</span>
          </div>
        ))}
        {mensuel > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, color: couleur, padding: '2px 0', fontWeight: 600 }}>
            <span>+ Nouvelle mensualité</span>
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtFCFA(mensuel)}</span>
          </div>
        )}
      </div>
      <div style={{ borderTop: `1px solid ${couleur}44`, paddingTop: 8, marginBottom: 6 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: 13 }}>
          <span>Total : {pct} % du salaire</span>
          <span style={{ color: couleur, fontVariantNumeric: 'tabular-nums' }}>{fmtFCFA(totalAvec)}</span>
        </div>
        <div style={{ fontSize: 12, color: T.ink3, marginTop: 3 }}>
          Capacité restante : <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtFCFA(Math.max(0, capacite_retenue - totalAvec))}</strong>
        </div>
      </div>
      <div style={{ fontSize: 12, fontWeight: 700, color: couleur }}>
        STATUT : {risque ? 'Risqué' : attention ? 'Attention' : 'Solvable'}
      </div>
      {risque && (
        <div style={{ fontSize: 12, color: '#E65100', marginTop: 4 }}>Validation DG recommandée</div>
      )}
    </div>
  );
}

// ── VueListe ──────────────────────────────────────────────────────────────
function VueListe({ dettes, setVue, setDetteActive, dashboard, token, chargerDonnees, setMsg }) {
  const [recherche, setRecherche] = useState('');
  const [filtreStatut, setFiltreStatut] = useState('');

  const all = toArray(dettes);
  const filtered = all.filter(d => {
    const q = recherche.toLowerCase();
    const matchQ = !q || (d.employe_nom || '').toLowerCase().includes(q) || (d.employe_matricule || '').includes(q);
    const matchS = !filtreStatut || d.statut === filtreStatut;
    return matchQ && matchS;
  });

  const selectStyle = {
    padding: '10px 28px 10px 12px', border: `1px solid ${T.line}`, borderRadius: 8,
    background: '#fff', minWidth: 140, cursor: 'pointer', fontSize: 13,
    appearance: 'none', WebkitAppearance: 'none',
    backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'><path d='M1 1l4 4 4-4' fill='none' stroke='%237A7A7A' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/></svg>")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 10px center',
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 20 }}>
        <div>
          <h1 style={{ margin: '0 0 4px', fontSize: 24, letterSpacing: '-0.01em', fontWeight: 700 }}>Prêts et retenues</h1>
          <p style={{ color: T.ink3, fontSize: 14, margin: 0 }}>Suivi des engagements financiers des employés</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Btn label="Nouveau prêt" icone="plus" primary onClick={() => setVue('nouveau')} />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
        <MetriqueCard icone="card"   label="Dettes actives"      valeur={dashboard.dettes_actives}       sous="validées + en cours" />
        <MetriqueCard icone="dollar" label="Volume actif"         valeur={fmt(dashboard.total_restant)}   sous="FCFA à recouvrer" />
        <MetriqueCard icone="clock"  label="En attente"           valeur={dashboard.en_attente_validation} sous="validations requises" pending />
        <MetriqueCard icone="alert"  label="Alertes seuil"        valeur={dashboard.alertes_seuil}        sous="dépassement 50%" alerte />
      </div>

      <div style={{ background: T.card, border: `0.5px solid ${T.line}`, borderRadius: T.radius,
        padding: 12, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14 }}>
        <div style={{ flex: '1 1 280px', minWidth: 220, position: 'relative' }}>
          <div style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: T.ink3 }}>
            <Ico name="search" size={15} color={T.ink3} />
          </div>
          <input
            value={recherche}
            onChange={e => setRecherche(e.target.value)}
            placeholder="Rechercher par nom ou matricule…"
            style={{ width: '100%', padding: '10px 12px 10px 36px', border: `1px solid ${T.line}`,
              borderRadius: 8, background: '#fff', fontSize: 13, boxSizing: 'border-box' }}
          />
        </div>
        <select value={filtreStatut} onChange={e => setFiltreStatut(e.target.value)} style={selectStyle}>
          <option value="">Tous statuts</option>
          <option value="en_attente">En attente</option>
          <option value="validee">Validée</option>
          <option value="active">Active</option>
          <option value="suspendue">Suspendue</option>
          <option value="soldee">Soldée</option>
          <option value="refusee">Refusée</option>
        </select>
      </div>

      <div className="sogas-dettes-wrap" style={{ background: T.card, border: `0.5px solid ${T.line}`, borderRadius: T.radius, boxShadow: T.shadow }}>
        <table className="sogas-dettes-table" style={{ minWidth: 1060, borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {['Employé', 'Type', 'Initial', 'Remboursé', 'Restant', 'Mensualité', 'Statut', ''].map((h, i) => (
                <th key={i} style={{ textAlign: i === 7 ? 'right' : 'left', fontWeight: 600, fontSize: 11.5,
                  textTransform: 'uppercase', letterSpacing: '.06em', color: T.ink3,
                  padding: '12px 16px', background: '#FAFAFA', borderBottom: `1px solid ${T.line}` }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={8} style={{ padding: '32px 16px', textAlign: 'center', color: T.ink3, fontSize: 14 }}>
                Aucune dette trouvée
              </td></tr>
            )}
            {filtered.map(d => (
              <LigneTableau key={d.id} dette={d} setVue={setVue} setDetteActive={setDetteActive}
                token={token} chargerDonnees={chargerDonnees} setMsg={setMsg} />
            ))}
          </tbody>
        </table>
        <div className="sogas-dettes-cards">
          {filtered.length === 0 && (
            <div style={{ padding: '32px 16px', textAlign: 'center', color: T.ink3, fontSize: 14 }}>Aucune dette trouvée</div>
          )}
          {filtered.map(d => (
            <CarteTableau key={d.id} dette={d} setVue={setVue} setDetteActive={setDetteActive}
              token={token} chargerDonnees={chargerDonnees} setMsg={setMsg} />
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 16px', fontSize: 12.5, color: T.ink3,
          borderTop: `1px solid ${T.line2}`, background: '#FAFAFA' }}>
          <div>Affichage {filtered.length} sur {all.length} engagements</div>
        </div>
      </div>
    </div>
  );
}

// ── LigneTableau ──────────────────────────────────────────────────────────
function LigneTableau({ dette: d, setVue, setDetteActive, token, chargerDonnees, setMsg }) {
  const [hover, setHover] = useState(false);
  const [loadingAction, setLoadingAction] = useState(false);
  const ouvrirDetail = () => setDetteActive(d);

  const handleValiderRapide = async (e) => {
    e.stopPropagation();
    setLoadingAction(true);
    try {
      await axios.patch(`${API}/dettes/${d.id}/valider`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setMsg({ texte: `Prêt ${d.reference} validé`, type: 'succes' });
      chargerDonnees();
    } catch (err) {
      setMsg({ texte: err.response?.data?.message || 'Erreur lors de la validation', type: 'erreur' });
    } finally {
      setLoadingAction(false);
    }
  };

  return (
    <tr onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      onClick={ouvrirDetail} style={{ cursor: 'pointer', background: hover ? '#FCFCFC' : 'transparent' }}>
      <td style={{ padding: '14px 16px', borderBottom: `1px solid ${T.line2}`, verticalAlign: 'middle' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 170 }}>
          <div style={{ width: 32, height: 32, borderRadius: '50%', background: T.greenSoft,
            color: T.green2, display: 'grid', placeItems: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
            {d.employe_initiales || '??'}
          </div>
          <div>
            <div style={{ fontWeight: 600, color: T.ink, fontSize: 13.5 }}>{d.employe_nom}</div>
            <div style={{ fontSize: 11.5, color: T.ink3, fontVariantNumeric: 'tabular-nums' }}>
              {d.employe_matricule} · {d.employe_site}
            </div>
          </div>
        </div>
      </td>
      <td style={{ padding: '14px 16px', borderBottom: `1px solid ${T.line2}`, verticalAlign: 'middle' }}>
        <BadgeType type={d.type_dette} libelle={d.type_libelle} />
      </td>
      <td style={{ padding: '14px 16px', borderBottom: `1px solid ${T.line2}`, verticalAlign: 'middle',
        fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', fontSize: 13.5 }}>
        {fmt(d.montant_initial)}
      </td>
      <td style={{ padding: '14px 16px', borderBottom: `1px solid ${T.line2}`, verticalAlign: 'middle',
        fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', color: T.ink3, fontSize: 13.5 }}>
        {fmt(d.montant_rembourse)}
      </td>
      <td style={{ padding: '14px 16px', borderBottom: `1px solid ${T.line2}`, verticalAlign: 'middle' }}>
        <BarreMini rembourse={d.montant_rembourse} initial={d.montant_initial}
          restant={d.montant_restant} statut={d.statut} />
      </td>
      <td style={{ padding: '14px 16px', borderBottom: `1px solid ${T.line2}`, verticalAlign: 'middle',
        fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', fontSize: 13.5,
        color: d.mensualite_actuelle === 0 ? T.ink3 : T.ink }}>
        {d.mensualite_actuelle > 0 ? fmt(d.mensualite_actuelle) : '—'}
      </td>
      <td style={{ padding: '14px 16px', borderBottom: `1px solid ${T.line2}`, verticalAlign: 'middle' }}>
        <BadgeStatut statut={d.statut} />
      </td>
      <td style={{ padding: '14px 16px', borderBottom: `1px solid ${T.line2}`, verticalAlign: 'middle' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
          {d.statut === 'en_attente' ? (
            <button
              onClick={handleValiderRapide}
              disabled={loadingAction}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '5px 10px', borderRadius: 8, border: `1px solid ${T.line}`, background: '#fff',
                cursor: loadingAction ? 'wait' : 'pointer', fontSize: 12, fontWeight: 600, color: T.green2,
                opacity: loadingAction ? 0.6 : 1 }}>
              <Ico name="check" size={12} color={T.green} stroke={2.5} />
              {loadingAction ? '…' : 'Valider'}
            </button>
          ) : (
            <IcoBtnTbl title="Voir le détail" icone="eye" onClick={() => ouvrirDetail()} />
          )}
        </div>
      </td>
    </tr>
  );
}

function CarteTableau({ dette: d, setVue, setDetteActive, token, chargerDonnees, setMsg }) {
  const [loadingAction, setLoadingAction] = useState(false);
  const ouvrirDetail = () => setDetteActive(d);

  const handleValiderRapide = async (e) => {
    e.stopPropagation();
    setLoadingAction(true);
    try {
      await axios.patch(`${API}/dettes/${d.id}/valider`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setMsg({ texte: `Prêt ${d.reference} validé`, type: 'succes' });
      chargerDonnees();
    } catch (err) {
      setMsg({ texte: err.response?.data?.message || 'Erreur lors de la validation', type: 'erreur' });
    } finally {
      setLoadingAction(false);
    }
  };

  return (
    <div onClick={ouvrirDetail} style={{ padding: '14px 16px', borderBottom: `1px solid ${T.line2}`, cursor: 'pointer' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <div style={{ width: 32, height: 32, borderRadius: '50%', background: T.greenSoft,
          color: T.green2, display: 'grid', placeItems: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
          {d.employe_initiales || '??'}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, color: T.ink, fontSize: 13.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.employe_nom}</div>
          <div style={{ fontSize: 11.5, color: T.ink3 }}>{d.employe_matricule} · {d.employe_site}</div>
        </div>
        <BadgeStatut statut={d.statut} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 10, textTransform: 'uppercase', color: T.ink4, letterSpacing: '.05em', marginBottom: 2 }}>Initial</div>
          <div style={{ fontSize: 13.5, fontVariantNumeric: 'tabular-nums', fontWeight: 500 }}>{fmt(d.montant_initial)}</div>
        </div>
        <div>
          <div style={{ fontSize: 10, textTransform: 'uppercase', color: T.ink4, letterSpacing: '.05em', marginBottom: 2 }}>Restant</div>
          <BarreMini rembourse={d.montant_rembourse} initial={d.montant_initial} restant={d.montant_restant} statut={d.statut} />
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }} onClick={e => e.stopPropagation()}>
        <BadgeType type={d.type_dette} libelle={d.type_libelle} />
        <div style={{ marginLeft: 'auto' }}>
          {d.statut === 'en_attente' ? (
            <button onClick={handleValiderRapide} disabled={loadingAction}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, minHeight: 40,
                padding: '8px 14px', borderRadius: 8, border: `1px solid ${T.line}`, background: '#fff',
                cursor: loadingAction ? 'wait' : 'pointer', fontSize: 13, fontWeight: 600, color: T.green2,
                opacity: loadingAction ? 0.6 : 1 }}>
              <Ico name="check" size={14} color={T.green} stroke={2.5} />
              {loadingAction ? '…' : 'Valider'}
            </button>
          ) : (
            <button onClick={ouvrirDetail}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 40,
                padding: '8px 14px', borderRadius: 8, border: `1px solid ${T.line}`, background: '#fff',
                cursor: 'pointer', fontSize: 13, fontWeight: 500, color: T.ink2 }}>
              Voir
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function IcoBtnTbl({ icone, title, onClick }) {
  return (
    <button title={title} onClick={onClick} style={{ width: 30, height: 30, borderRadius: 7,
      border: `1px solid ${T.line}`, background: '#fff', display: 'grid', placeItems: 'center',
      cursor: 'pointer', color: T.ink2 }}>
      <Ico name={icone} size={14} color={T.ink2} />
    </button>
  );
}

// ── VueDetail ─────────────────────────────────────────────────────────────
function VueDetail({ dette: d, setVue, onReload, chargerDonnees, setMsg }) {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [panneau, setPanneau] = useState(null);
  const [loading, setLoading] = useState(false);

  const [montantPaiement, setMontantPaiement] = useState('');
  const [datePaiement, setDatePaiement] = useState(aujourdhui());
  const [commentairePaiement, setCommentairePaiement] = useState('');

  const [nouvelleMensualite, setNouvelleMensualite] = useState('');

  const [motifSuspension, setMotifSuspension] = useState('');
  const [motifRefus, setMotifRefus] = useState('');

  if (!d) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      padding: '80px 24px', textAlign: 'center' }}>
      <div style={{ width: 56, height: 56, borderRadius: 16, background: T.greenSoft, display: 'grid',
        placeItems: 'center', marginBottom: 18 }}>
        <Ico name="file" size={26} color={T.green} />
      </div>
      <div style={{ fontSize: 17, fontWeight: 600, color: T.ink, marginBottom: 8 }}>Aucune dette sélectionnée</div>
      <div style={{ fontSize: 14, color: T.ink3, marginBottom: 24, maxWidth: 320 }}>
        Sélectionnez une dette dans la liste pour voir le détail.
      </div>
      <Btn label="Retour à la liste" icone="back" onClick={() => setVue('liste')} />
    </div>
  );

  const p = pctCalc(d.montant_rembourse, d.montant_initial);
  const paiements = toArray(d.paiements);
  const headers = { Authorization: `Bearer ${token}` };

  const fermerPanneau = () => {
    setPanneau(null);
    setMontantPaiement(''); setDatePaiement(aujourdhui()); setCommentairePaiement('');
    setNouvelleMensualite(''); setMotifSuspension(''); setMotifRefus('');
  };

  const handlePaiement = async () => {
    if (!montantPaiement || parseFloat(montantPaiement) <= 0) {
      setMsg({ texte: 'Montant invalide', type: 'erreur' }); return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/dettes/${d.id}/paiements`,
        { montant: parseFloat(montantPaiement), date_paie: datePaiement, commentaire: commentairePaiement },
        { headers });
      const solde = res.data.solde;
      setMsg({ texte: solde ? 'Paiement enregistré — dette soldée automatiquement' : `Paiement enregistré. Restant : ${fmtFCFA(res.data.montant_restant)}`, type: 'succes' });
      fermerPanneau();
      onReload(d.id);
      chargerDonnees();
    } catch (err) {
      setMsg({ texte: err.response?.data?.message || 'Erreur paiement', type: 'erreur' });
    } finally { setLoading(false); }
  };

  const handleMensualite = async () => {
    if (!nouvelleMensualite || parseFloat(nouvelleMensualite) <= 0) {
      setMsg({ texte: 'Mensualité invalide', type: 'erreur' }); return;
    }
    setLoading(true);
    try {
      const res = await axios.patch(`${API}/dettes/${d.id}/mensualite`,
        { mensualite: parseFloat(nouvelleMensualite) }, { headers });
      setMsg({ texte: `Mensualité mise à jour — ${res.data.mois_restants} mois restants`, type: 'succes' });
      fermerPanneau();
      onReload(d.id);
    } catch (err) {
      setMsg({ texte: err.response?.data?.message || 'Erreur modification mensualité', type: 'erreur' });
    } finally { setLoading(false); }
  };

  const handleValider = async () => {
    setLoading(true);
    try {
      await axios.patch(`${API}/dettes/${d.id}/valider`, {}, { headers });
      setMsg({ texte: 'Prêt validé avec succès', type: 'succes' });
      fermerPanneau();
      onReload(d.id);
      chargerDonnees();
    } catch (err) {
      setMsg({ texte: err.response?.data?.message || 'Erreur validation', type: 'erreur' });
    } finally { setLoading(false); }
  };

  const handleSuspendre = async () => {
    setLoading(true);
    try {
      const res = await axios.patch(`${API}/dettes/${d.id}/suspendre`,
        { motif: motifSuspension }, { headers });
      setMsg({ texte: `Dette ${res.data.statut === 'suspendue' ? 'suspendue' : 'reprise'}`, type: 'succes' });
      fermerPanneau();
      onReload(d.id);
      chargerDonnees();
    } catch (err) {
      setMsg({ texte: err.response?.data?.message || 'Erreur', type: 'erreur' });
    } finally { setLoading(false); }
  };

  const handleRefuser = async () => {
    if (!motifRefus.trim()) {
      setMsg({ texte: 'Le motif de refus est obligatoire', type: 'erreur' }); return;
    }
    setLoading(true);
    try {
      await axios.patch(`${API}/dettes/${d.id}/refuser`, { motif: motifRefus }, { headers });
      setMsg({ texte: 'Demande refusée', type: 'succes' });
      fermerPanneau();
      chargerDonnees();
      setVue('liste');
    } catch (err) {
      setMsg({ texte: err.response?.data?.message || 'Erreur refus', type: 'erreur' });
    } finally { setLoading(false); }
  };

  const peutPayer     = ['validee', 'active'].includes(d.statut);
  const peutSuspendre = ['active', 'suspendue'].includes(d.statut);
  const estSuspendu   = d.statut === 'suspendue';
  const estEnAttente  = d.statut === 'en_attente';

  const inputPanStyle = { width: '100%', padding: '9px 12px', border: `1px solid ${T.line}`,
    borderRadius: 8, background: '#fff', fontSize: 13, boxSizing: 'border-box' };
  const labelPanStyle = { fontSize: 12, fontWeight: 600, color: T.ink2, display: 'block', marginBottom: 4 };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 20 }}>
        <div style={{ flex: 1 }}>
          <BreadcrumbRow chemin={`${d.employe_nom} · ${d.type_libelle}`} setVue={setVue} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 4 }}>
            <div style={{ width: 52, height: 52, borderRadius: '50%', background: T.greenSoft, color: T.green2,
              display: 'grid', placeItems: 'center', fontSize: 18, fontWeight: 700, flexShrink: 0 }}>
              {d.employe_initiales || '??'}
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 18, fontWeight: 700, letterSpacing: '-0.005em' }}>
                {d.employe_nom}
                <BadgeStatut statut={d.statut} />
              </div>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 13, color: T.ink3, marginTop: 4 }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  <Ico name="card" size={12} color={T.ink3} />Matricule {d.employe_matricule}
                </span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  <Ico name="map" size={12} color={T.ink3} />{d.employe_site}
                </span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  <Ico name="card" size={12} color={T.ink3} />{d.reference || '—'}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Btn label="Retour" icone="back" onClick={() => setVue('liste')} />
          {d.type_dette_code === 'pret_sogas' && (
            <Btn label="Dossier SOGAS" icone="file" onClick={() => navigate('/prets-sogas')} />
          )}
          {peutPayer && (
            <Btn label="Enregistrer un paiement" icone="plus" primary
              onClick={() => setPanneau(panneau === 'paiement' ? null : 'paiement')} />
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.1fr) minmax(0, 1fr)', gap: 16 }}>
        <div>
          <Card>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8, color: T.ink }}>
              <Ico name="calendar" size={16} color={T.ink3} />Informations du prêt
              <span style={{ marginLeft: 'auto', fontSize: 12, color: T.ink3, fontWeight: 500 }}>
                {d.reference}
              </span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 18px' }}>
              {[
                ['Type',         d.type_libelle],
                ['Motif',        d.motif || '—'],
                ['Initial',      fmtFCFA(d.montant_initial)],
                ['Mensualité',   d.mensualite_actuelle > 0 ? fmtFCFA(d.mensualite_actuelle) : '—'],
                ['Remboursé',    fmtFCFA(d.montant_rembourse)],
                ['Restant',      fmtFCFA(d.montant_restant)],
                ['Début',        d.date_debut ? new Date(d.date_debut).toLocaleDateString('fr-FR') : '—'],
                ['Fin prévue',   d.date_fin_prevue ? new Date(d.date_fin_prevue).toLocaleDateString('fr-FR') : '—'],
              ].map(([k, v], i) => (
                <div key={i}>
                  <div style={{ fontSize: 11.5, color: T.ink3, textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 3 }}>{k}</div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: k === 'Restant' ? T.green : T.ink, fontVariantNumeric: 'tabular-nums' }}>{v}</div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 18, paddingTop: 18, borderTop: `1px solid ${T.line2}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
                <strong style={{ fontSize: 22, fontVariantNumeric: 'tabular-nums' }}>{p} %</strong>
                <span style={{ fontSize: 12, color: T.ink3 }}>
                  remboursé{d.mois_restants ? ` · ${d.mois_restants} mois restants` : ''}
                </span>
              </div>
              <div style={{ height: 10, background: '#EFEFEF', borderRadius: 99, overflow: 'hidden' }}>
                <div style={{ width: p + '%', height: '100%', background: T.green, borderRadius: 99 }} />
              </div>
            </div>
          </Card>

          {/* Actions */}
          <Card style={{ marginTop: 14 }}>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Ico name="edit" size={16} color={T.ink3} />Actions disponibles
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {peutPayer && (
                <Btn label="Enregistrer un paiement" icone="payment" primary fullWidth
                  onClick={() => setPanneau(panneau === 'paiement' ? null : 'paiement')} />
              )}
              {peutPayer && (
                <Btn label="Modifier la mensualité" icone="edit" fullWidth
                  onClick={() => setPanneau(panneau === 'mensualite' ? null : 'mensualite')} />
              )}
              {peutSuspendre && (
                <Btn label={estSuspendu ? 'Reprendre' : 'Suspendre'} icone={estSuspendu ? 'play' : 'pause'} fullWidth
                  onClick={() => setPanneau(panneau === 'suspendre' ? null : 'suspendre')} />
              )}
              {estEnAttente && (
                <Btn label="Valider" icone="check" primary fullWidth
                  onClick={() => setPanneau(panneau === 'valider' ? null : 'valider')} />
              )}
              {estEnAttente && (
                <Btn label="Refuser" icone="x" danger fullWidth
                  onClick={() => setPanneau(panneau === 'refuser' ? null : 'refuser')} />
              )}
            </div>

            {/* Panneau paiement */}
            {panneau === 'paiement' && (
              <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${T.line2}` }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: T.ink, marginBottom: 10 }}>Enregistrer un paiement</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                  <div>
                    <label style={labelPanStyle}>Montant (FCFA)</label>
                    <input type="number" value={montantPaiement} onChange={e => setMontantPaiement(e.target.value)}
                      placeholder={d.mensualite_actuelle > 0 ? String(d.mensualite_actuelle) : ''}
                      style={inputPanStyle} />
                  </div>
                  <div>
                    <label style={labelPanStyle}>Date</label>
                    <input type="date" value={datePaiement} onChange={e => setDatePaiement(e.target.value)} style={inputPanStyle} />
                  </div>
                </div>
                <div style={{ marginBottom: 10 }}>
                  <label style={labelPanStyle}>Commentaire (optionnel)</label>
                  <input type="text" value={commentairePaiement} onChange={e => setCommentairePaiement(e.target.value)}
                    placeholder="Retenue sur paie…" style={inputPanStyle} />
                </div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <Btn label="Annuler" ghost onClick={fermerPanneau} />
                  <Btn label={loading ? '…' : 'Confirmer'} primary onClick={handlePaiement} />
                </div>
              </div>
            )}

            {/* Panneau mensualite */}
            {panneau === 'mensualite' && (
              <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${T.line2}` }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: T.ink, marginBottom: 10 }}>Modifier la mensualité</div>
                <div style={{ marginBottom: 10 }}>
                  <label style={labelPanStyle}>Nouvelle mensualité (FCFA)</label>
                  <input type="number" value={nouvelleMensualite} onChange={e => setNouvelleMensualite(e.target.value)}
                    placeholder={d.mensualite_actuelle > 0 ? String(d.mensualite_actuelle) : ''}
                    style={inputPanStyle} />
                  {nouvelleMensualite > 0 && d.montant_restant > 0 && (
                    <div style={{ fontSize: 12, color: T.ink3, marginTop: 4 }}>
                      Durée restante estimée : {Math.ceil(d.montant_restant / parseFloat(nouvelleMensualite))} mois
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <Btn label="Annuler" ghost onClick={fermerPanneau} />
                  <Btn label={loading ? '…' : 'Mettre à jour'} primary onClick={handleMensualite} />
                </div>
              </div>
            )}

            {/* Panneau suspendre */}
            {panneau === 'suspendre' && (
              <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${T.line2}` }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: T.ink, marginBottom: 8 }}>
                  {estSuspendu ? 'Reprendre le prélèvement' : 'Suspendre le prélèvement'}
                </div>
                <div style={{ marginBottom: 10 }}>
                  <label style={labelPanStyle}>Motif (optionnel)</label>
                  <input type="text" value={motifSuspension} onChange={e => setMotifSuspension(e.target.value)}
                    placeholder="Raison de la suspension…" style={inputPanStyle} />
                </div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <Btn label="Annuler" ghost onClick={fermerPanneau} />
                  <Btn label={loading ? '…' : estSuspendu ? 'Reprendre' : 'Suspendre'} onClick={handleSuspendre} />
                </div>
              </div>
            )}

            {/* Panneau valider */}
            {panneau === 'valider' && (
              <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${T.line2}` }}>
                <div style={{ fontSize: 13, color: T.ink, marginBottom: 10 }}>
                  Confirmer la validation de ce prêt ?
                </div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <Btn label="Annuler" ghost onClick={fermerPanneau} />
                  <Btn label={loading ? '…' : 'Valider'} primary onClick={handleValider} />
                </div>
              </div>
            )}

            {/* Panneau refuser */}
            {panneau === 'refuser' && (
              <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${T.line2}` }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: T.red, marginBottom: 8 }}>Refuser cette demande</div>
                <div style={{ marginBottom: 10 }}>
                  <label style={labelPanStyle}>Motif de refus (obligatoire)</label>
                  <textarea value={motifRefus} onChange={e => setMotifRefus(e.target.value)}
                    placeholder="Expliquez la raison du refus…"
                    style={{ ...inputPanStyle, minHeight: 72, resize: 'vertical' }} />
                </div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <Btn label="Annuler" ghost onClick={fermerPanneau} />
                  <Btn label={loading ? '…' : 'Confirmer le refus'} danger onClick={handleRefuser} />
                </div>
              </div>
            )}
          </Card>
        </div>

        <div>
          <Card>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Ico name="history" size={16} color={T.ink3} />Historique des paiements
              <span style={{ marginLeft: 'auto', fontSize: 12, color: T.ink3, fontWeight: 500 }}>
                {paiements.length} retenue{paiements.length !== 1 ? 's' : ''}
              </span>
            </div>
            {paiements.length === 0 ? (
              <p style={{ color: T.ink3, fontSize: 13, margin: 0 }}>Aucun paiement enregistré</p>
            ) : (
              <div className="sogas-paie-wrap">
                <table className="sogas-paie-table" style={{ borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      {['Date', 'Montant', 'Commentaire'].map(h => (
                        <th key={h} style={{ textAlign: 'left', fontSize: 11.5, textTransform: 'uppercase',
                          letterSpacing: '.05em', color: T.ink3, padding: '10px 0',
                          borderBottom: `1px solid ${T.line2}`, fontWeight: 600 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {paiements.map((p2, i) => (
                      <tr key={i}>
                        <td style={{ padding: '12px 0', borderBottom: i < paiements.length - 1 ? `1px solid ${T.line2}` : 0, fontSize: 13.5 }}>{p2.date}</td>
                        <td style={{ padding: '12px 0', borderBottom: i < paiements.length - 1 ? `1px solid ${T.line2}` : 0, fontSize: 13.5, fontWeight: 600, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{fmtFCFA(p2.montant)}</td>
                        <td style={{ padding: '12px 0', borderBottom: i < paiements.length - 1 ? `1px solid ${T.line2}` : 0, fontSize: 13.5, color: T.ink2 }}>{p2.commentaire}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="sogas-paie-cards">
                  {paiements.map((p2, i) => (
                    <div key={i} style={{ padding: '10px 0', borderBottom: i < paiements.length - 1 ? `1px solid ${T.line2}` : 0 }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: p2.commentaire ? 6 : 0 }}>
                        <div>
                          <div style={{ fontSize: 10, textTransform: 'uppercase', color: T.ink4, letterSpacing: '.05em', marginBottom: 2 }}>Date</div>
                          <div style={{ fontSize: 13.5 }}>{p2.date}</div>
                        </div>
                        <div>
                          <div style={{ fontSize: 10, textTransform: 'uppercase', color: T.ink4, letterSpacing: '.05em', marginBottom: 2 }}>Montant</div>
                          <div style={{ fontSize: 13.5, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{fmtFCFA(p2.montant)}</div>
                        </div>
                      </div>
                      {p2.commentaire && (
                        <div style={{ fontSize: 13, color: T.ink2 }}>{p2.commentaire}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}

// ── VueFormulaire ─────────────────────────────────────────────────────────
function VueFormulaire({ setVue, chargerDonnees, setMsg }) {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [types, setTypes] = useState([]);
  const [employe, setEmploye] = useState(null);
  const [solvabilite, setSolvabilite] = useState(null);
  const [loadingSolvabilite, setLoadingSolvabilite] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [erreurForm, setErreurForm] = useState('');

  const [typeDette, setTypeDette]     = useState('');
  const [montant, setMontant]         = useState('');
  const [mensualite, setMensualite]   = useState('');
  const [dateDebut, setDateDebut]     = useState(aujourdhui());
  const [motif, setMotif]             = useState('');
  const [montantFacture, setMontantFacture] = useState('');
  const [tauxIPM, setTauxIPM]         = useState(60);
  const [beneficiaire, setBeneficiaire] = useState('employe');

  useEffect(() => {
    axios.get(`${API}/dettes/types`, { headers })
      .then(res => {
        const liste = toArray(res.data.types);
        setTypes(liste);
        if (liste.length > 0) setTypeDette(liste[0].code);
      })
      .catch(() => {});
  }, [token]);

  useEffect(() => {
    if (!employe) { setSolvabilite(null); return; }
    setLoadingSolvabilite(true);
    axios.get(`${API}/dettes/employe/${employe.id}/solvabilite`, { headers })
      .then(res => setSolvabilite(res.data))
      .catch(() => setSolvabilite(null))
      .finally(() => setLoadingSolvabilite(false));
  }, [employe, token]);

  const typeSelectionne = types.find(t => t.code === typeDette);
  const estIPM = typeSelectionne?.gestionnaire === 'ipm';

  const montantIPMcharge = estIPM && montantFacture ? Math.round(parseFloat(montantFacture) * (1 - tauxIPM / 100)) : 0;
  const montantIPMpris   = estIPM && montantFacture ? Math.round(parseFloat(montantFacture) * tauxIPM / 100) : 0;
  const montantEffectif  = estIPM ? montantIPMcharge : parseFloat(montant) || 0;
  const mensualiteNum    = parseFloat(mensualite) || 0;

  const finPrevue = () => {
    if (!mensualiteNum || !montantEffectif) return '—';
    const mois = Math.ceil(montantEffectif / mensualiteNum);
    const d = new Date(dateDebut || Date.now());
    d.setMonth(d.getMonth() + mois);
    return d.toLocaleDateString('fr-FR');
  };

  const handleSoumettre = async () => {
    setErreurForm('');
    if (!employe) { setErreurForm('Veuillez sélectionner un employé'); return; }
    if (!typeDette) { setErreurForm('Veuillez choisir un type de dette'); return; }
    if (!motif.trim()) { setErreurForm('Le motif est obligatoire'); return; }
    const montantFinal = estIPM ? montantIPMcharge : parseFloat(montant);
    if (!montantFinal || montantFinal <= 0) { setErreurForm('Montant invalide'); return; }

    const type = types.find(t => t.code === typeDette);
    if (!type) { setErreurForm('Type invalide'); return; }

    const body = {
      employe_id: employe.id,
      type_dette_id: type.id,
      montant_initial: montantFinal,
      mensualite: mensualiteNum || undefined,
      date_debut: dateDebut || undefined,
      motif: motif.trim(),
    };
    if (estIPM) {
      body.montant_facture = parseFloat(montantFacture);
      body.taux_ipm = tauxIPM;
      body.beneficiaire_ipm = beneficiaire;
    }

    setLoadingSubmit(true);
    try {
      const res = await axios.post(`${API}/dettes`, body, { headers });
      const avertissement = res.data.avertissement_solvabilite;
      setMsg({ texte: `Prêt créé — référence ${res.data.reference}${avertissement ? ' (seuil 50% dépassé)' : ''}`, type: 'succes' });
      chargerDonnees();
      setVue('liste');
    } catch (err) {
      setErreurForm(err.response?.data?.message || 'Erreur lors de la création');
    } finally {
      setLoadingSubmit(false);
    }
  };

  const fieldStyle = { display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 };
  const labelStyle = { fontSize: 12.5, fontWeight: 600, color: T.ink2 };
  const inputStyle = { width: '100%', padding: '10px 12px', border: `1px solid ${T.line}`, borderRadius: 8, background: '#fff', fontSize: 13, boxSizing: 'border-box' };

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <BreadcrumbRow chemin="Nouveau prêt / retenue" setVue={setVue} />
        <h1 style={{ margin: '0 0 4px', fontSize: 24, fontWeight: 700 }}>Nouveau prêt / retenue</h1>
        <p style={{ color: T.ink3, fontSize: 14, margin: 0 }}>Créez un engagement financier pour un employé</p>
      </div>

      {erreurForm && (
        <div style={{ background: T.redSoft, border: `0.5px solid #EF9A9A`, borderRadius: 8,
          padding: '10px 16px', color: T.red, fontSize: 13, marginBottom: 16 }}>
          {erreurForm}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.3fr) minmax(0, 1fr)', gap: 16 }}>
        <Card>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Ico name="file" size={16} color={T.ink3} />Informations de la dette
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Employé</label>
            <RechercheEmploye employe={employe} onSelect={setEmploye} token={token} />
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Type de dette</label>
            {types.length === 0 ? (
              <div style={{ color: T.ink3, fontSize: 13, padding: '10px 12px', border: `1px solid ${T.line}`, borderRadius: 8 }}>Chargement…</div>
            ) : (
              <select value={typeDette} onChange={e => setTypeDette(e.target.value)} style={inputStyle}>
                {types.map(t => (
                  <option key={t.id} value={t.code}>{t.libelle}</option>
                ))}
              </select>
            )}
          </div>

          {estIPM && (
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div style={fieldStyle}>
                  <label style={labelStyle}>Montant de la facture</label>
                  <input type="number" value={montantFacture}
                    onChange={e => setMontantFacture(e.target.value)}
                    style={inputStyle} placeholder="100000" />
                  <span style={{ fontSize: 12, color: T.ink3 }}>Montant total de la prestation</span>
                </div>
                <div style={fieldStyle}>
                  <label style={labelStyle}>Taux IPM (%)</label>
                  <input type="number" value={tauxIPM}
                    onChange={e => setTauxIPM(parseFloat(e.target.value) || 60)}
                    style={inputStyle} min={0} max={100} />
                </div>
              </div>
              {montantFacture > 0 && (
                <div style={{ background: '#F0F5F0', border: '1px solid #D9E6DA', borderRadius: 10,
                  padding: '12px 14px', fontSize: 13, color: T.ink2, display: 'flex', gap: 10,
                  alignItems: 'flex-start', marginBottom: 14 }}>
                  <Ico name="calcok" size={16} color={T.green} />
                  <div>
                    IPM prend en charge : <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtFCFA(montantIPMpris)}</strong><br />
                    Reste à la charge de l'employé : <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtFCFA(montantIPMcharge)}</strong>
                  </div>
                </div>
              )}
              <div style={fieldStyle}>
                <label style={labelStyle}>Bénéficiaire</label>
                <select value={beneficiaire} onChange={e => setBeneficiaire(e.target.value)} style={inputStyle}>
                  <option value="employe">Employé</option>
                  <option value="conjoint">Conjoint(e)</option>
                  <option value="enfant">Enfant</option>
                  <option value="parent">Parent à charge</option>
                </select>
              </div>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div style={fieldStyle}>
              <label style={labelStyle}>
                Montant {estIPM && <span style={{ fontWeight: 400, color: T.ink4 }}>(auto-rempli)</span>}
              </label>
              <input type="number"
                value={estIPM ? montantIPMcharge : montant}
                onChange={e => !estIPM && setMontant(e.target.value)}
                readOnly={estIPM}
                style={{ ...inputStyle, background: estIPM ? '#fafafa' : '#fff' }} />
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>Mensualité prévue</label>
              <input type="number" value={mensualite} onChange={e => setMensualite(e.target.value)} style={inputStyle} />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div style={fieldStyle}>
              <label style={labelStyle}>Date de début</label>
              <input type="date" value={dateDebut} onChange={e => setDateDebut(e.target.value)} style={inputStyle} />
              {mensualiteNum > 0 && montantEffectif > 0 && (
                <span style={{ fontSize: 12, color: T.ink3 }}>Fin prévue : {finPrevue()}</span>
              )}
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>Motif</label>
              <input type="text" value={motif} onChange={e => setMotif(e.target.value)}
                placeholder="Raison du prêt…" style={inputStyle} />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end',
            paddingTop: 18, marginTop: 18, borderTop: `1px solid ${T.line2}` }}>
            <Btn label="Annuler" ghost onClick={() => setVue('liste')} />
            <Btn label={loadingSubmit ? 'Création…' : 'Soumettre pour validation'} primary onClick={handleSoumettre} />
          </div>
        </Card>

        <div>
          {employe && (
            <CardSolvabilite
              solvabilite={solvabilite}
              loading={loadingSolvabilite}
              mensualite={mensualiteNum}
            />
          )}

          {!employe && (
            <div style={{ border: `1px solid ${T.line}`, borderRadius: 12, padding: '20px 18px',
              background: T.card, color: T.ink3, fontSize: 13, textAlign: 'center' }}>
              Sélectionnez un employé pour voir sa solvabilité
            </div>
          )}

          {solvabilite && (
            <Card style={{ marginTop: 14 }}>
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Ico name="card" size={16} color={T.ink3} />Engagements actifs
              </div>
              {toArray(solvabilite.detail_par_gestionnaire).length === 0 ? (
                <p style={{ color: T.ink3, fontSize: 13, margin: 0 }}>Aucun engagement en cours</p>
              ) : (
                <div className="sogas-engag-wrap">
                  <table className="sogas-engag-table" style={{ borderCollapse: 'collapse' }}>
                    <tbody>
                      {toArray(solvabilite.detail_par_gestionnaire).map((d, i) => (
                        <tr key={d.gestionnaire}>
                          <td style={{ padding: '10px 0', borderBottom: i < solvabilite.detail_par_gestionnaire.length - 1 ? `1px solid ${T.line2}` : 0, fontSize: 13.5 }}>
                            {d.gestionnaire === 'rh' ? 'RH' : d.gestionnaire === 'oeuvre_sociale' ? 'Oeuvre Sociale' : 'IPM'}
                          </td>
                          <td style={{ padding: '10px 0', borderBottom: i < solvabilite.detail_par_gestionnaire.length - 1 ? `1px solid ${T.line2}` : 0, fontSize: 13.5, fontWeight: 600, fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>
                            {fmtFCFA(d.total_gestionnaire)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="sogas-engag-cards">
                    {toArray(solvabilite.detail_par_gestionnaire).map((d, i) => (
                      <div key={d.gestionnaire} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 0', borderBottom: i < solvabilite.detail_par_gestionnaire.length - 1 ? `1px solid ${T.line2}` : 0 }}>
                        <div style={{ flex: 1, fontSize: 13.5 }}>
                          {d.gestionnaire === 'rh' ? 'RH' : d.gestionnaire === 'oeuvre_sociale' ? 'Oeuvre Sociale' : 'IPM'}
                        </div>
                        <div style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', fontSize: 13.5 }}>{fmtFCFA(d.total_gestionnaire)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          )}

          <Card style={{ marginTop: 14 }}>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Ico name="shield" size={16} color={T.ink3} />Politique de la société
            </div>
            <ul style={{ margin: 0, paddingLeft: 18, color: T.ink2, fontSize: 13, lineHeight: 1.6 }}>
              <li>Seuil d'alerte : 50 % du salaire net</li>
              <li>Au-delà de 50 %, validation DG recommandée</li>
              <li>Le remboursement commence à la paie suivante</li>
              <li>IPM : l'employé paie uniquement sa part</li>
            </ul>
          </Card>
        </div>
      </div>
    </div>
  );
}

// ── Composants partagés ───────────────────────────────────────────────────
function Card({ children, style = {} }) {
  return (
    <div style={{ background: T.card, border: `0.5px solid ${T.line}`, borderRadius: T.radius,
      padding: 18, boxShadow: T.shadow, ...style }}>
      {children}
    </div>
  );
}

function Btn({ label, icone, primary, danger, ghost, fullWidth, onClick }) {
  let bg = '#fff', color = T.ink, border = `1px solid ${T.line}`;
  if (primary) { bg = T.green; color = '#fff'; border = `1px solid ${T.green}`; }
  if (danger)  { bg = '#fff';  color = T.red;  border = '1px solid #E6C7C7'; }
  if (ghost)   { bg = 'transparent'; color = T.ink2; border = '1px solid transparent'; }
  return (
    <button onClick={onClick} style={{ display: 'inline-flex', alignItems: 'center', gap: 8,
      padding: '10px 16px', borderRadius: 10, border, fontWeight: 600, cursor: 'pointer',
      fontSize: 13.5, background: bg, color, width: fullWidth ? '100%' : undefined,
      justifyContent: fullWidth ? 'flex-start' : 'center', fontFamily: 'inherit' }}>
      {icone && <Ico name={icone} size={14} color={color} />}
      {label}
    </button>
  );
}

// ── Normalisation réponse API → format attendu par les composants ─────────
function normaliserDette(d) {
  const prenom = d.prenom || '';
  const nom = d.nom || '';
  return {
    ...d,
    employe_nom: `${prenom} ${nom}`.trim() || d.employe_nom || '—',
    employe_matricule: d.matricule || d.employe_matricule || '—',
    employe_site: d.site_nom || d.employe_site || '—',
    employe_initiales: (`${prenom[0] || ''}${nom[0] || ''}`).toUpperCase() || d.employe_initiales || '??',
    type_dette: d.type_dette_code || d.type_dette || '',
    type_libelle: d.type_dette_libelle || d.type_libelle || '—',
    mensualite_actuelle: parseFloat(d.mensualite) || d.mensualite_actuelle || 0,
    paiements: toArray(d.paiements),
    prochaines_retenues: [],
  };
}

function normaliserDashboard(stats) {
  if (!stats) return { dettes_actives: 0, total_restant: 0, en_attente_validation: 0, alertes_seuil: 0 };
  return {
    dettes_actives: parseInt(stats.active || 0) + parseInt(stats.validee || 0),
    total_restant: parseFloat(stats.volume_actif || 0),
    en_attente_validation: parseInt(stats.en_attente || 0),
    alertes_seuil: 0,
  };
}

// ── Page principale ───────────────────────────────────────────────────────
export default function DettesPage() {
  const { token } = useAuth();
  const [vue, setVue] = useState('liste');
  const [detteActive, setDetteActive] = useState(null);
  const [dettes, setDettes] = useState([]);
  const [dashboard, setDashboard] = useState({ dettes_actives: 0, total_restant: 0, en_attente_validation: 0, alertes_seuil: 0 });
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState({ texte: '', type: '' });

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!msg.texte) return;
    const t = setTimeout(() => setMsg({ texte: '', type: '' }), 3000);
    return () => clearTimeout(t);
  }, [msg]);

  const chargerDonnees = useCallback(async () => {
    setLoading(true);
    try {
      const [listeRes, dashRes] = await Promise.all([
        axios.get(`${API}/dettes`, { headers }),
        axios.get(`${API}/dettes/dashboard`, { headers }),
      ]);
      setDettes(toArray(listeRes.data.dettes).map(normaliserDette));
      setDashboard(normaliserDashboard(dashRes.data.stats));
    } catch (_) {}
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { chargerDonnees(); }, [chargerDonnees]);

  const ouvrirDetail = useCallback(async (detteOuId) => {
    const id = typeof detteOuId === 'string' ? detteOuId : detteOuId?.id;
    if (!id) return;
    try {
      const res = await axios.get(`${API}/dettes/${id}`, { headers });
      const { dette: d, paiements } = res.data;
      setDetteActive({
        ...normaliserDette(d),
        paiements: toArray(paiements).map(p => ({
          date: new Date(p.created_at).toLocaleDateString('fr-FR'),
          montant: parseFloat(p.montant),
          commentaire: p.commentaire || '—',
        })),
      });
      setVue('detail');
    } catch (_) {
      if (typeof detteOuId === 'object') { setDetteActive(detteOuId); setVue('detail'); }
    }
  }, [token]);

  return (
    <Layout>
      <style>{`
        .sogas-dettes-wrap { overflow-x: auto; }
        .sogas-dettes-table { width: 100%; border-collapse: collapse; }
        .sogas-dettes-cards { display: none; }
        .sogas-paie-wrap { overflow-x: auto; }
        .sogas-paie-table { width: 100%; border-collapse: collapse; }
        .sogas-paie-cards { display: none; }
        .sogas-engag-wrap { overflow-x: auto; }
        .sogas-engag-table { width: 100%; border-collapse: collapse; }
        .sogas-engag-cards { display: none; }
        @media (max-width: 767px) {
          .sogas-dettes-wrap { overflow-x: visible; }
          .sogas-dettes-table { display: none; }
          .sogas-dettes-cards { display: block; }
          .sogas-paie-wrap { overflow-x: visible; }
          .sogas-paie-table { display: none; }
          .sogas-paie-cards { display: block; }
          .sogas-engag-wrap { overflow-x: visible; }
          .sogas-engag-table { display: none; }
          .sogas-engag-cards { display: block; }
        }
      `}</style>
      <div style={{ minHeight: '100vh', background: T.bg, fontFamily: 'system-ui, -apple-system, sans-serif', fontSize: 14 }}>
        <Topbar vue={vue} setVue={setVue} />
        <div style={{ padding: 24, maxWidth: 1400, margin: '0 auto' }}>
          <BanniereMsg msg={msg} />
          {loading && vue === 'liste' && (
            <div style={{ textAlign: 'center', padding: 60, color: T.ink3, fontSize: 14 }}>Chargement…</div>
          )}
          {!loading && vue === 'liste' && (
            <VueListe
              dettes={dettes}
              setVue={setVue}
              setDetteActive={ouvrirDetail}
              dashboard={dashboard}
              token={token}
              chargerDonnees={chargerDonnees}
              setMsg={setMsg}
            />
          )}
          {vue === 'detail' && (
            <VueDetail
              dette={detteActive}
              setVue={setVue}
              onReload={ouvrirDetail}
              chargerDonnees={chargerDonnees}
              setMsg={setMsg}
            />
          )}
          {vue === 'nouveau' && (
            <VueFormulaire setVue={setVue} chargerDonnees={chargerDonnees} setMsg={setMsg} />
          )}
        </div>
      </div>
    </Layout>
  );
}
